import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AdminLayout } from './AdminLayout';
import { Button } from './ui/button';
import { 
  Mail, 
  Calendar, 
  Trash2, 
  CheckCircle, 
  Circle,
  RefreshCw,
  MessageSquare,
  Inbox,
  Phone,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [username, setUsername] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const storedUsername = localStorage.getItem('admin_username');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    setUsername(storedUsername || 'Admin');
    
    const loadContacts = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/contacts?token=${token}`);
        
        if (response.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/login');
          return;
        }
        
        const data = await response.json();
        setContacts(data);
      } catch (err) {
        console.error('Failed to fetch contacts:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadContacts();
  }, [navigate]);

  const fetchContacts = async (token) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contacts?token=${token}`);
      
      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/login');
        return;
      }
      
      const data = await response.json();
      setContacts(data);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (contactId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/contacts/${contactId}/read?token=${token}`, {
        method: 'PUT',
      });
      setContacts(contacts.map(c => 
        c.id === contactId ? { ...c, is_read: true } : c
      ));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm(language === 'tr' ? 'Bu mesajı silmek istediğinize emin misiniz?' : 'Are you sure you want to delete this message?')) {
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/contacts/${contactId}?token=${token}`, {
        method: 'DELETE',
      });
      setContacts(contacts.filter(c => c.id !== contactId));
      if (selectedContact?.id === contactId) {
        setSelectedContact(null);
      }
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const texts = {
    tr: {
      title: 'İletişim Mesajları',
      noMessages: 'Henüz mesaj yok',
      selectMessage: 'Detayları görmek için bir mesaj seçin',
      from: 'Gönderen',
      email: 'E-posta',
      date: 'Tarih',
      message: 'Mesaj',
      markRead: 'Okundu İşaretle',
      delete: 'Sil',
      refresh: 'Yenile',
      unread: 'okunmamış',
      back: 'Geri',
      reply: 'Yanıtla',
    },
    en: {
      title: 'Contact Messages',
      noMessages: 'No messages yet',
      selectMessage: 'Select a message to view details',
      from: 'From',
      email: 'Email',
      date: 'Date',
      message: 'Message',
      markRead: 'Mark as Read',
      delete: 'Delete',
      refresh: 'Refresh',
      unread: 'unread',
      back: 'Back',
      reply: 'Reply',
    },
  };

  const t = texts[language];
  const unreadCount = contacts.filter(c => !c.is_read).length;

  // Mobile: Show detail view when contact is selected
  const MobileDetailView = () => (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      {/* Back button header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-800">
        <button
          onClick={() => setSelectedContact(null)}
          className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" strokeWidth={1.5} />
        </button>
        <span className="font-medium text-slate-900 dark:text-white truncate">
          {selectedContact.name}
        </span>
      </div>

      {/* Contact details */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {selectedContact.name}
            </h2>
            <div className="flex flex-col gap-1.5">
              {selectedContact.phone && (
                <a 
                  href={`tel:${selectedContact.phone}`}
                  className="text-green-600 dark:text-green-400 hover:underline text-sm flex items-center gap-1.5"
                  data-testid="contact-phone"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  {selectedContact.phone.replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3')}
                </a>
              )}
              <a 
                href={`mailto:${selectedContact.email}`}
                className="text-indigo-600 dark:text-indigo-400 hover:underline text-sm flex items-center gap-1.5"
                data-testid="contact-email"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                {selectedContact.email}
              </a>
              <div className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                <Calendar className="w-4 h-4" strokeWidth={1.5} />
                {formatDate(selectedContact.created_at)}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => deleteContact(selectedContact.id)}
            className="text-slate-400 hover:text-red-600 dark:hover:text-red-400"
            data-testid="delete-contact"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
            {t.message}
          </div>
          <p className="text-slate-600 dark:text-slate-400 whitespace-pre-wrap text-sm" data-testid="contact-message">
            {selectedContact.message}
          </p>
        </div>

        <a 
          href={`mailto:${selectedContact.email}?subject=Re: AgrosTech İletişim Formu`}
          className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-colors"
          data-testid="reply-button"
        >
          <Mail className="w-4 h-4" strokeWidth={1.5} />
          {t.reply}
        </a>
      </div>
    </div>
  );

  // Mobile: Show list view
  const MobileListView = () => (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      {isLoading ? (
        <div className="p-8 text-center">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="p-8 text-center">
          <Inbox className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-3" strokeWidth={1} />
          <p className="text-slate-500 dark:text-slate-400">{t.noMessages}</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {contacts.map((contact) => (
            <button
              key={contact.id}
              onClick={() => {
                setSelectedContact(contact);
                if (!contact.is_read) markAsRead(contact.id);
              }}
              className="w-full p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              data-testid={`contact-item-${contact.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  {contact.is_read ? (
                    <CheckCircle className="w-4 h-4 text-slate-300 dark:text-slate-600" strokeWidth={1.5} />
                  ) : (
                    <Circle className="w-4 h-4 text-indigo-600 dark:text-indigo-400 fill-current" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium block truncate ${
                    contact.is_read 
                      ? 'text-slate-600 dark:text-slate-400' 
                      : 'text-slate-900 dark:text-white'
                  }`}>
                    {contact.name}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-500 truncate mt-0.5">
                    {contact.email}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-1">
                    {contact.message.substring(0, 50)}...
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    {formatDate(contact.created_at)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout username={username}>
      {/* Title & Actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white" data-testid="contacts-title">{t.title}</h1>
          {unreadCount > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full">
              {unreadCount} {t.unread}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchContacts(localStorage.getItem('admin_token'))}
          className="border-slate-200 dark:border-slate-700"
          data-testid="refresh-button"
        >
          <RefreshCw className="w-4 h-4 sm:mr-2" strokeWidth={1.5} />
          <span className="hidden sm:inline">{t.refresh}</span>
        </Button>
      </div>

      {/* Mobile View - Single Card */}
      <div className="lg:hidden">
        {selectedContact ? <MobileDetailView /> : <MobileListView />}
      </div>

      {/* Desktop View - Two Column Grid */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center">
              <Inbox className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-3" strokeWidth={1} />
              <p className="text-slate-500 dark:text-slate-400">{t.noMessages}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[600px] overflow-y-auto">
              {contacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => {
                    setSelectedContact(contact);
                    if (!contact.is_read) markAsRead(contact.id);
                  }}
                  className={`w-full p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                    selectedContact?.id === contact.id ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                  }`}
                  data-testid={`contact-item-${contact.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {contact.is_read ? (
                        <CheckCircle className="w-4 h-4 text-slate-300 dark:text-slate-600" strokeWidth={1.5} />
                      ) : (
                        <Circle className="w-4 h-4 text-indigo-600 dark:text-indigo-400 fill-current" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-medium truncate ${
                          contact.is_read 
                            ? 'text-slate-600 dark:text-slate-400' 
                            : 'text-slate-900 dark:text-white'
                        }`}>
                          {contact.name}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-500 truncate mt-0.5">
                        {contact.email}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-1">
                        {contact.message.substring(0, 50)}...
                      </p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                        {formatDate(contact.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
          {selectedContact ? (
            <div>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">
                    {selectedContact.name}
                  </h2>
                  <div className="flex flex-col gap-1">
                    {selectedContact.phone && (
                      <a 
                        href={`tel:${selectedContact.phone}`}
                        className="text-green-600 dark:text-green-400 hover:underline text-sm flex items-center gap-1"
                        data-testid="contact-phone-desktop"
                      >
                        <Phone className="w-3 h-3" strokeWidth={1.5} />
                        {selectedContact.phone.replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3')}
                      </a>
                    )}
                    <a 
                      href={`mailto:${selectedContact.email}`}
                      className="text-indigo-600 dark:text-indigo-400 hover:underline text-sm flex items-center gap-1"
                      data-testid="contact-email-desktop"
                    >
                      <Mail className="w-3 h-3" strokeWidth={1.5} />
                      {selectedContact.email}
                    </a>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteContact(selectedContact.id)}
                  className="text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                  data-testid="delete-contact-desktop"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Calendar className="w-4 h-4" strokeWidth={1.5} />
                  {formatDate(selectedContact.created_at)}
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                    {t.message}
                  </div>
                  <p className="text-slate-600 dark:text-slate-400 whitespace-pre-wrap" data-testid="contact-message-desktop">
                    {selectedContact.message}
                  </p>
                </div>

                <div className="pt-4">
                  <a 
                    href={`mailto:${selectedContact.email}?subject=Re: AgrosTech İletişim Formu`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                    data-testid="reply-button-desktop"
                  >
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                    {t.reply}
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-center py-16">
              <div>
                <MessageSquare className="w-16 h-16 text-slate-200 dark:text-slate-700 mx-auto mb-4" strokeWidth={1} />
                <p className="text-slate-500 dark:text-slate-400">{t.selectMessage}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
